import {
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query'

import { ActionFields } from '@tribeplatform/gql-client/global-types'
import type { ClientError } from '@tribeplatform/gql-client/lib'
import {
  Action,
  Event,
  MutationRegisterForEventArgs,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useTribeClient } from '../../useTribeClient.js'

import { getEventKey, getEventRecurrencesKey } from './event.key.js'
import { getQueriesContainingEvent } from './utils.js'

export const useRegisterForEvent = (options?: {
  fields?: ActionFields
  useMutationOptions?: UseMutationOptions<
    Action,
    ClientError,
    MutationRegisterForEventArgs,
    { snapshot: [QueryKey, InfiniteData<Event>][] }
  >
}) => {
  const { useMutationOptions, fields } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()

  return useMutation<
    Action,
    ClientError,
    MutationRegisterForEventArgs,
    { snapshot: [QueryKey, InfiniteData<Event>][] }
  >(
    (input: MutationRegisterForEventArgs) =>
      client.events.registerForEvent(input, fields),
    {
      ...useMutationOptions,
      onSuccess: (_, variables, context) => {
        queryClient.invalidateQueries(
          getEventKey({ variables: { id: variables.eventId } }),
        )
        const event = queryClient.getQueryData<Event>(
          getEventKey({ variables: { id: variables.eventId } }),
        )

        queryClient.invalidateQueries(
          getEventRecurrencesKey({
            variables: { recurrenceId: event?.recurrenceId },
          }),
        )

        getQueriesContainingEvent(queryClient, variables.eventId).forEach(
          query => queryClient.invalidateQueries(query.queryKey),
        )
        useMutationOptions?.onSuccess?.(_, variables, context)
      },
    },
  )
}
